import { useState, useMemo } from 'react'
import { format, parseISO, differenceInHours } from 'date-fns'
import { useHealthLogs, useAllLogs, useAllergies } from './hooks/useHealth'
import { useMembers } from '../../hooks/useMembers'
import { useAuth } from '../../hooks/useAuth'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

const SEVERITY_LABELS = { 1: 'Mild', 2: 'Moderate', 3: 'Significant', 4: 'Severe', 5: 'Extreme' }
const SEVERITY_COLORS = { 1: 'bg-yellow-100 text-yellow-700', 2: 'bg-orange-100 text-orange-700',
  3: 'bg-red-100 text-red-700', 4: 'bg-rose-200 text-rose-800', 5: 'bg-red-200 text-red-900' }

const Modal = ({ title, onClose, children }) => (
  <Portal>
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
        <h2 className="text-base font-bold text-text-primary">{title}</h2>
        <button onClick={onClose} className="text-text-muted p-1">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">{children}</div>
    </div>
  </div>
  </Portal>
)

// ─── Insights: food–symptom correlation ───────────────────────────────────────
const InsightsTab = () => {
  const { data: allLogs = [], isLoading } = useAllLogs()

  const correlations = useMemo(() => {
    const counts = {}
    for (const log of allLogs) {
      const symptoms   = log.symptoms   ?? []
      const foods      = log.foods_eaten ?? []
      const logDate    = parseISO(log.log_date)

      // For each symptom, look at logs 0–2 days before for food occurrences
      for (const sym of symptoms) {
        const prior = allLogs.filter((l) => {
          const d = parseISO(l.log_date)
          const diff = differenceInHours(logDate, d)
          return diff >= 0 && diff <= 48 && l.member_id === log.member_id
        })
        for (const priorLog of prior) {
          for (const food of priorLog.foods_eaten ?? []) {
            const key = `${food.name}|||${sym.name}`
            counts[key] = (counts[key] ?? 0) + 1
          }
        }
      }
    }
    return Object.entries(counts)
      .filter(([, n]) => n > 2)
      .sort(([, a], [, b]) => b - a)
      .map(([key, n]) => {
        const [food, symptom] = key.split('|||')
        return { food, symptom, count: n }
      })
  }, [allLogs])

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>

  return (
    <div className="py-2">
      <p className="text-xs text-text-muted mb-4">
        Foods that appeared within 48 hours before a symptom more than twice over the last 90 days.
      </p>
      {correlations.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-8">Not enough data yet. Keep logging!</p>
      ) : (
        <div className="space-y-2">
          {correlations.map(({ food, symptom, count }) => (
            <div key={`${food}-${symptom}`} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="text-sm font-semibold text-text-primary">{food}</div>
              <div className="text-xs text-text-muted mt-0.5">
                Preceded <em>{symptom}</em> {count} time{count !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Log entry form ───────────────────────────────────────────────────────────
const LogForm = ({ memberId, members, isAdmin, onClose }) => {
  const { upsertLog } = useHealthLogs(memberId)
  const [logDate,   setLogDate]   = useState(format(new Date(), 'yyyy-MM-dd'))
  const [targetMember, setTargetMember] = useState(memberId)
  const [symptoms,  setSymptoms]  = useState([{ name: '', severity: 1 }])
  const [foods,     setFoods]     = useState([{ name: '', portion: '' }])
  const [notes,     setNotes]     = useState('')
  const [saving,    setSaving]    = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await upsertLog({
        log_date:    logDate,
        member_id:   targetMember,
        symptoms:    symptoms.filter((s) => s.name.trim()),
        foods_eaten: foods.filter((f) => f.name.trim()),
        notes:       notes || null,
      })
      onClose()
    } finally { setSaving(false) }
  }

  return (
    <Modal title="Log Health Entry" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Date</label>
            <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          {isAdmin && (
            <div>
              <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Member</label>
              <select value={targetMember} onChange={(e) => setTargetMember(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                {members.filter((m) => m.profiles).map((m) => (
                  <option key={m.profiles.id} value={m.profiles.id}>{m.profiles.display_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Symptoms */}
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2 block">Symptoms</label>
          <div className="space-y-2">
            {symptoms.map((s, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" placeholder="Symptom" value={s.name} onChange={(e) => setSymptoms((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  className="flex-1 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
                <select value={s.severity} onChange={(e) => setSymptoms((p) => p.map((x, j) => j === i ? { ...x, severity: parseInt(e.target.value) } : x))}
                  className="w-28 rounded-lg border border-border px-2 py-1.5 text-sm">
                  {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} – {SEVERITY_LABELS[n]}</option>)}
                </select>
                <button type="button" onClick={() => setSymptoms((p) => p.filter((_, j) => j !== i))} className="text-text-muted hover:text-rose-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setSymptoms((p) => [...p, { name: '', severity: 1 }])} className="mt-1 text-xs text-primary font-medium">+ Add symptom</button>
        </div>

        {/* Foods */}
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2 block">Foods Eaten</label>
          <div className="space-y-2">
            {foods.map((f, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" placeholder="Food" value={f.name} onChange={(e) => setFoods((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  className="flex-1 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
                <input type="text" placeholder="Portion" value={f.portion} onChange={(e) => setFoods((p) => p.map((x, j) => j === i ? { ...x, portion: e.target.value } : x))}
                  className="w-24 rounded-lg border border-border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
                <button type="button" onClick={() => setFoods((p) => p.filter((_, j) => j !== i))} className="text-text-muted hover:text-rose-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setFoods((p) => [...p, { name: '', portion: '' }])} className="mt-1 text-xs text-primary font-medium">+ Add food</button>
        </div>

        <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          className="w-full rounded-base border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary" />

        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>{saving ? <Spinner size="sm" /> : 'Save Log'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Allergies section ────────────────────────────────────────────────────────
const AllergySection = ({ memberId, members, isAdmin }) => {
  const { data: allergies = [], addAllergy, deleteAllergy } = useAllergies()
  const [newAllergen, setNewAllergen] = useState('')
  const [severity,    setSeverity]    = useState('mild')
  const [adding,      setAdding]      = useState(false)
  const [showForm,    setShowForm]    = useState(false)

  const mine = allergies.filter((a) => a.member_id === memberId)

  const submit = async (e) => {
    e.preventDefault()
    if (!newAllergen.trim()) return
    setAdding(true)
    try {
      await addAllergy({ member_id: memberId, allergen: newAllergen.trim(), severity })
      setNewAllergen(''); setShowForm(false)
    } finally { setAdding(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide">Known Allergies</h3>
        <button onClick={() => setShowForm((v) => !v)} className="text-xs text-primary font-medium">+ Add</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="flex gap-2 mb-3">
          <input type="text" placeholder="Allergen" value={newAllergen} onChange={(e) => setNewAllergen(e.target.value)} autoFocus
            className="flex-1 rounded-xl border border-border px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}
            className="rounded-xl border border-border px-2 py-1.5 text-sm">
            {['mild','moderate','severe','anaphylactic'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button type="submit" size="sm" disabled={adding}>{adding ? <Spinner size="sm" /> : 'Add'}</Button>
        </form>
      )}

      {mine.length === 0 ? (
        <p className="text-xs text-text-muted">No allergies recorded</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {mine.map((a) => (
            <div key={a.id} className="flex items-center gap-1.5 bg-rose-50 border border-rose-200 rounded-full px-3 py-1">
              <span className="text-xs font-semibold text-rose-700">{a.allergen}</span>
              <span className="text-[10px] text-rose-500">{a.severity}</span>
              <button onClick={() => deleteAllergy(a.id)} className="text-rose-400 hover:text-rose-700 ml-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Member log view ──────────────────────────────────────────────────────────
const MemberLogs = ({ memberId, members, isAdmin }) => {
  const { data: logs = [], isLoading, deleteLog } = useHealthLogs(memberId)
  const [showForm, setShowForm] = useState(false)

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-text-muted">{logs.length} log{logs.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1 text-sm text-primary font-semibold">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          Add Log
        </button>
      </div>

      <AllergySection memberId={memberId} members={members} isAdmin={isAdmin} />
      <div className="mt-5 space-y-3">
        {logs.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">No logs yet</p>
        ) : logs.map((log) => (
          <div key={log.id} className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-text-primary">{format(parseISO(log.log_date), 'EEEE, MMM d')}</span>
              <button onClick={() => deleteLog(log.id)} className="text-text-muted hover:text-rose-500">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>

            {(log.symptoms ?? []).length > 0 && (
              <div className="mb-2">
                <div className="text-xs font-semibold text-text-muted mb-1.5">Symptoms</div>
                <div className="flex flex-wrap gap-1.5">
                  {log.symptoms.map((s, i) => (
                    <span key={i} className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', SEVERITY_COLORS[s.severity] ?? 'bg-gray-100 text-gray-600')}>
                      {s.name} ({s.severity})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(log.foods_eaten ?? []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-text-muted mb-1">Foods eaten</div>
                <div className="text-xs text-text-secondary">
                  {log.foods_eaten.map((f, i) => (
                    <span key={i}>{f.name}{f.portion ? ` (${f.portion})` : ''}{i < log.foods_eaten.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
              </div>
            )}

            {log.notes && <p className="text-xs text-text-muted mt-2 italic">{log.notes}</p>}
          </div>
        ))}
      </div>

      {showForm && (
        <LogForm memberId={memberId} members={members} isAdmin={isAdmin} onClose={() => setShowForm(false)} />
      )}
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export const HealthPage = () => {
  const { user, isAdmin } = useAuth()
  const { data: members = [] } = useMembers()
  const [tab, setTab] = useState('logs')

  const visibleMembers = isAdmin
    ? members.filter((m) => m.profiles)
    : members.filter((m) => m.profiles?.id === user?.id)

  const [activeMemberId, setActiveMemberId] = useState(null)
  const effectiveMemberId = activeMemberId ?? visibleMembers[0]?.profiles?.id ?? user?.id

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-4">Health</h1>

      {/* Member tabs */}
      {isAdmin && visibleMembers.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {visibleMembers.map((m) => (
            <button
              key={m.profiles.id}
              onClick={() => setActiveMemberId(m.profiles.id)}
              className={cn('flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors',
                effectiveMemberId === m.profiles.id ? 'text-white' : 'bg-background text-text-muted hover:bg-border')}
              style={effectiveMemberId === m.profiles.id ? { backgroundColor: m.profiles.member_color ?? '#2d1b5e' } : {}}
            >
              {m.profiles.display_name}
            </button>
          ))}
        </div>
      )}

      {/* Feature tabs */}
      <div className="flex gap-1 mb-5 bg-background rounded-xl p-1">
        {['logs', 'insights'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={cn('flex-1 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors',
              tab === t ? 'bg-surface text-primary shadow-sm' : 'text-text-muted')}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'logs' && (
        <MemberLogs memberId={effectiveMemberId} members={members} isAdmin={isAdmin} />
      )}
      {tab === 'insights' && <InsightsTab />}
    </div>
  )
}
